import { match, P } from 'ts-pattern';
import { z } from 'zod';
import { json, preprocessFormData, zfd } from 'zod-form-data';
import { GspClaimReason } from '~/utils/constants';
import { logError } from '~/utils/observability';
import {
  makeShippingProtectionClaimAction,
  makeShippingProtectionClaimLoader,
  redirect,
} from '~/utils/routing';
import { mediaAssetSchema } from '~/utils/schemas';

type Context = Parameters<
  Parameters<typeof makeShippingProtectionClaimLoader>[0]
>[0]['context'];
type AppStore = Parameters<
  Parameters<typeof makeShippingProtectionClaimLoader>[0]
>[1];

const getRequiredImgUploadCount = ({
  order,
  shippingProtectionClaim,
}: Pick<Context, 'order' | 'shippingProtectionClaim'>) =>
  Number(
    order.isShippingClaimImgRequired &&
      shippingProtectionClaim.reason === GspClaimReason.damaged,
  );

export const { loader, useLoaderData } = makeShippingProtectionClaimLoader(
  ({ context }) =>
    match(context.shippingProtectionClaim.reason)
      .with(GspClaimReason.damaged, () => ({
        uploadFileInfo: {
          storefrontId: context.order.storefrontId,
          destination: 'gspClaimImg' as const,
          requiredImgUploadCount: getRequiredImgUploadCount(context),
        },
        ...context.shippingProtectionClaim.meta,
      }))
      .with(P.nonNullable, () => ({
        uploadFileInfo: undefined,
        ...context.shippingProtectionClaim.meta,
      }))
      .otherwise(() => {
        logError(
          new Error(
            'Shipping protection claim reason is not defined at meta loader.',
          ),
        );
        // back to the order page if the reason is not defined
        return redirect('../..');
      }),
);

const makeFormSchema = (minAssets = 0) =>
  zfd.formData(
    z.object({
      noteFromCustomer: zfd.text(z.string().optional()),
      assets: zfd.repeatable(
        z.array(json(mediaAssetSchema)).min(minAssets, {
          message: `A minimum of ${minAssets} media files are required`,
        }),
      ),
    }),
  );

const handleFormDataError = (formData: FormData) => (error: z.ZodError) => {
  const minAssetsIssue = error.issues.find(
    (issue) => issue.path.includes('assets') && issue.code === 'too_small',
  );

  return minAssetsIssue ?
      {
        ok: false,
        message: minAssetsIssue.message,
      }
    : new Error('Malformed form data', {
        cause: {
          error,
          formData: preprocessFormData(formData),
        },
      });
};

const handleFormData =
  (appStore: AppStore) =>
  ({
    noteFromCustomer,
    assets,
  }: z.infer<ReturnType<typeof makeFormSchema>>) => {
    appStore.dispatch(
      appStore.actions.shippingProtectionClaim.setMeta({
        noteFromCustomer,
        assets,
      }),
    );

    return redirect('../address');
  };

export const { action, useActionResult } = makeShippingProtectionClaimAction(
  ({ formData, context }, appStore) =>
    match(
      makeFormSchema(getRequiredImgUploadCount(context)).safeParse(formData),
    )
      .with({ success: true, data: P.select() }, handleFormData(appStore))
      .with(
        { success: false, error: P.select() },
        handleFormDataError(formData),
      )
      .exhaustive(),
);
